import {
  formatDate,
  recoveryCodesSchema,
  twoFactorConfirmSchema,
  twoFactorSetupSchema,
  twoFactorStatusSchema,
  type TwoFactorSetup,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, PasswordInput, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCurrentUser } from '@/lib/auth/session';
import { RecoveryCodesView, TotpSetupForm } from '@/features/auth/two-factor-setup';
import { zodFieldErrors, type FieldErrors } from '@/lib/forms';

const STATUS_KEY = ['auth', 'two-factor'];

/** Minha conta › Verificação em duas etapas (RF-03): ativar, códigos de recuperação, desativar. */
export function DoisFatoresPage() {
  const { client } = useAuth();
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [starting, setStarting] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [dialog, setDialog] = useState<'regenerate' | 'disable' | null>(null);

  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: ({ signal }) => client.request('/auth/2fa', { schema: twoFactorStatusSchema, signal }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: STATUS_KEY });

  async function start() {
    setStarting(true);
    try {
      setSetup(await client.request('/auth/2fa/setup', { method: 'POST', schema: twoFactorSetupSchema }));
    } catch (err) {
      toast.error('Não foi possível começar a ativação', { description: errorMessage(err) });
    } finally {
      setStarting(false);
    }
  }

  let body: React.ReactNode;
  if (codes) {
    body = (
      <RecoveryCodesView
        codes={codes}
        onDone={() => {
          setCodes(null);
          void refresh();
        }}
      />
    );
  } else if (setup) {
    body = (
      <TotpSetupForm
        setup={setup}
        account={user.email}
        activate={async (code) =>
          (
            await client.request('/auth/2fa/enable', {
              method: 'POST',
              body: { code },
              schema: recoveryCodesSchema,
            })
          ).recoveryCodes
        }
        onCancel={() => setSetup(null)}
        onEnabled={(recoveryCodes) => {
          setSetup(null);
          setCodes(recoveryCodes);
          toast.success('Verificação em duas etapas ativada');
        }}
      />
    );
  } else if (status.isPending) {
    body = <LoadingState rows={3} label="Carregando a verificação em duas etapas" />;
  } else if (status.isError) {
    body = (
      <ErrorState error={status.error} onRetry={() => void status.refetch()} retrying={status.isFetching} />
    );
  } else if (!status.data.enabled) {
    body = (
      <section aria-label="Situação" className="rounded-lg border border-border bg-surface p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-bold">Verificação em duas etapas</h2>
          <Badge variant="neutral">Desativada</Badge>
        </div>
        {status.data.required ? (
          <div className="mt-3">
            <FormAlert tone="locked" title="Exigida para o seu perfil">
              Sua empresa exige a verificação em duas etapas. Ative agora; no próximo login ela será pedida de
              qualquer forma.
            </FormAlert>
          </div>
        ) : null}
        <p className="mt-2 max-w-prose text-sm text-muted-foreground">
          Além da senha, o Fluxus pede um código de 6 números gerado pelo app autenticador do seu celular.
          Mesmo que alguém descubra sua senha, não entra sem o celular.
        </p>
        <Button className="mt-5" loading={starting} onClick={() => void start()}>
          <ShieldCheck aria-hidden />
          Ativar verificação em duas etapas
        </Button>
      </section>
    );
  } else {
    const remaining = status.data.recoveryCodesRemaining;
    body = (
      <section aria-label="Situação" className="rounded-lg border border-border bg-surface p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-bold">Verificação em duas etapas</h2>
          <Badge variant="brand">Ativa</Badge>
          {status.data.required ? <Badge variant="outline">Exigida pelo seu perfil</Badge> : null}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Ativa desde {status.data.enabledAt ? formatDate(status.data.enabledAt) : '—'}. Ao entrar, o Fluxus
          pede o código do app autenticador.
        </p>
        <div className="mt-4">
          {remaining <= 3 ? (
            <FormAlert tone="locked" title="Poucos códigos de recuperação">
              {remaining === 0
                ? 'Você não tem mais códigos de recuperação. Gere novos para não ficar sem acesso se perder o celular.'
                : `Restam ${remaining} códigos de recuperação. Gere novos para não ficar sem acesso se perder o celular.`}
            </FormAlert>
          ) : (
            <p className="text-sm">
              <KeyRound className="mr-1.5 inline size-4 text-muted-foreground" aria-hidden />
              {remaining} códigos de recuperação disponíveis.
            </p>
          )}
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={() => setDialog('regenerate')}>
            <KeyRound aria-hidden />
            Gerar novos códigos de recuperação
          </Button>
          {status.data.required ? null : (
            <Button variant="outline" onClick={() => setDialog('disable')}>
              <ShieldOff aria-hidden />
              Desativar
            </Button>
          )}
        </div>
        {status.data.required ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Sua empresa exige a verificação em duas etapas para o seu perfil, por isso ela não pode ser
            desativada.
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Verificação em duas etapas"
        description="Proteja sua conta com um código do celular além da senha."
      />
      <div className="mt-6">{body}</div>
      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title={
          dialog === 'disable'
            ? 'Desativar a verificação em duas etapas'
            : 'Gerar novos códigos de recuperação'
        }
        description={
          dialog === 'disable'
            ? 'Sua conta volta a ser protegida só pela senha. Confirme com a senha e um código.'
            : 'Os códigos atuais deixam de valer. Confirme com a senha e um código.'
        }
      >
        {dialog ? (
          <ConfirmForm
            action={dialog}
            onCancel={() => setDialog(null)}
            onDone={(recoveryCodes) => {
              setDialog(null);
              if (recoveryCodes) setCodes(recoveryCodes);
              else toast.success('Verificação em duas etapas desativada');
              void refresh();
            }}
          />
        ) : null}
      </Dialog>
    </PageContainer>
  );
}

/** Senha + código (app ou recuperação) para gerar novos códigos ou desativar. */
function ConfirmForm({
  action,
  onCancel,
  onDone,
}: {
  action: 'regenerate' | 'disable';
  onCancel: () => void;
  onDone: (codes?: string[]) => void;
}) {
  const { client } = useAuth();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { password: useId(), code: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = twoFactorConfirmSchema.safeParse({ password, code });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      if (action === 'regenerate') {
        const res = await client.request('/auth/2fa/recovery-codes', {
          method: 'POST',
          body: parsed.data,
          schema: recoveryCodesSchema,
        });
        onDone(res.recoveryCodes);
      } else {
        await client.request('/auth/2fa/disable', { method: 'POST', body: parsed.data });
        onDone();
      }
    } catch (err) {
      setFormError(err);
      setCode('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.password} label="Senha" error={errors.password}>
        <PasswordInput
          {...fieldA11y(ids.password, errors.password)}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </FormField>
      <FormField id={ids.code} label="Código do app autenticador ou de recuperação" error={errors.code}>
        <Input
          {...fieldA11y(ids.code, errors.code)}
          value={code}
          autoComplete="one-time-code"
          maxLength={20}
          onChange={(e) => setCode(e.target.value)}
          className="h-11 font-mono"
        />
      </FormField>
      {formError ? (
        <FormAlert
          tone={formError instanceof ApiError && formError.code === 'ACCOUNT_LOCKED' ? 'locked' : 'error'}
        >
          {errorMessage(formError)}
        </FormAlert>
      ) : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" variant={action === 'disable' ? 'danger' : 'primary'} loading={saving}>
          {action === 'disable' ? 'Desativar' : 'Gerar novos códigos'}
        </Button>
      </div>
    </form>
  );
}
